import { useState, useEffect, useCallback } from 'react'
import { BarChart3 } from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
} from 'recharts'
import { fetchAppAnalytics } from '@/services/project-api'
import type { AppAnalyticsData } from '@/services/project-api'

const ANALYTICS_SERIES_COLOR = 'hsl(var(--chart-1, 201 96% 32%))'

interface AppAnalyticsProps {
  slug: string
  appName: string
}

export function AppAnalytics({ slug, appName }: AppAnalyticsProps) {
  const [range, setRange] = useState<'7d' | '30d'>('7d')
  const [data, setData] = useState<AppAnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchAppAnalytics(slug, range)
      setData(result)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [slug, range])

  useEffect(() => {
    load()
  }, [load])

  // ─── Loading state ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="mt-3 p-4 rounded-lg border border-border bg-card space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-4 w-40 bg-muted rounded animate-pulse" />
          <div className="flex gap-1">
            <div className="h-7 w-16 bg-muted rounded-full animate-pulse" />
            <div className="h-7 w-16 bg-muted rounded-full animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="h-16 bg-muted rounded animate-pulse" />
          <div className="h-16 bg-muted rounded animate-pulse" />
          <div className="h-16 bg-muted rounded animate-pulse" />
        </div>
        <div className="space-y-2">
          <div className="h-4 w-24 bg-muted rounded animate-pulse" />
          <div className="h-4 bg-muted rounded animate-pulse" />
          <div className="h-4 bg-muted rounded animate-pulse" />
          <div className="h-4 bg-muted rounded animate-pulse" />
        </div>
      </div>
    )
  }

  // ─── Error state ────────────────────────────────────────────
  if (error) {
    return (
      <div className="mt-3 p-4 rounded-lg border border-border bg-card text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Unable to load analytics. Check your connection and try again.
        </p>
        <button
          onClick={load}
          className="rounded-lg border border-border bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:opacity-90"
        >
          Retry
        </button>
      </div>
    )
  }

  // ─── Empty state ────────────────────────────────────────────
  // Null-safe — the analytics endpoint can return a payload without
  // `metrics` (e.g. when the app has no telemetry bucket yet). Treat
  // that as the empty state instead of throwing on property access.
  if (
    data &&
    (data.metrics?.page_views ?? 0) === 0 &&
    (data.metrics?.events ?? 0) === 0 &&
    (data.metrics?.errors ?? 0) === 0
  ) {
    return (
      <div className="mt-3 p-4 rounded-lg border border-border bg-card">
        {/* Header with range toggle */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-foreground">{appName} Analytics</span>
          <RangeToggle range={range} onChange={setRange} />
        </div>
        <div className="text-center py-8 space-y-2">
          <BarChart3 className="w-8 h-8 text-muted-foreground/40 mx-auto" />
          <p className="text-sm font-medium text-foreground">No analytics yet</p>
          <p className="text-xs text-muted-foreground max-w-xs mx-auto">
            Analytics will appear after your app receives its first visit. Share your app URL to start collecting data.
          </p>
        </div>
      </div>
    )
  }

  // ─── Data state ─────────────────────────────────────────────
  const metrics = data?.metrics ?? { page_views: 0, events: 0, errors: 0 }

  // Recharts data transforms
  const trendData = (data?.daily_breakdown ?? []).map((d) => ({
    date: d.date.slice(5), // "MM-DD"
    pageViews: d.page_views,
    events: d.events,
    errors: d.errors,
  }))

  const topPagesData = (data?.top_pages ?? []).slice(0, 8).map((p) => ({
    path: p.path.length > 20 ? p.path.slice(0, 20) + '\u2026' : p.path,
    views: p.count,
  }))

  const errorSparkData = (data?.daily_breakdown ?? []).map((d) => d.errors)

  return (
    <div className="mt-3 p-4 rounded-lg border border-border bg-card space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{appName} Analytics</span>
        <RangeToggle range={range} onChange={setRange} />
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Page Views" value={metrics.page_views} />
        <MetricCard label="Events" value={metrics.events} />
        <MetricCard label="Errors" value={metrics.errors} sparkData={errorSparkData} />
      </div>

      {/* Daily Trend — AreaChart */}
      {data && data.daily_breakdown.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Daily Trend
          </h4>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={trendData} margin={{ top: 4, right: 0, left: -24, bottom: 0 }}>
              <defs>
                <linearGradient id="colorPageViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ANALYTICS_SERIES_COLOR} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={ANALYTICS_SERIES_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.08} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Area
                type="monotone"
                dataKey="pageViews"
                name="Page Views"
                stroke={ANALYTICS_SERIES_COLOR}
                fill="url(#colorPageViews)"
                strokeWidth={2}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Pages — BarChart vertical */}
      {data && data.top_pages.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Top Pages
          </h4>
          <ResponsiveContainer
            width="100%"
            height={Math.max(120, topPagesData.length * 28)}
          >
            <BarChart
              data={topPagesData}
              layout="vertical"
              margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
            >
              <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis
                type="category"
                dataKey="path"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={90}
              />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="views" name="Views" fill={ANALYTICS_SERIES_COLOR} fillOpacity={0.8} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────

function RangeToggle({
  range,
  onChange,
}: {
  range: '7d' | '30d'
  onChange: (r: '7d' | '30d') => void
}) {
  return (
    <div className="flex gap-1">
      <button
        onClick={() => onChange('7d')}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
          range === '7d'
            ? 'bg-foreground text-background'
            : 'bg-muted text-muted-foreground hover:text-foreground'
        }`}
      >
        7 Days
      </button>
      <button
        onClick={() => onChange('30d')}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
          range === '30d'
            ? 'bg-foreground text-background'
            : 'bg-muted text-muted-foreground hover:text-foreground'
        }`}
      >
        30 Days
      </button>
    </div>
  )
}

function MetricCard({
  label,
  value,
  sparkData,
}: {
  label: string
  value: number
  sparkData?: number[]
}) {
  return (
    <div className="p-3 rounded-lg bg-muted/50 flex flex-col gap-1">
      <p className="text-2xl font-semibold text-foreground">{value.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sparkData && sparkData.length > 1 && (
        <LineChart width={48} height={20} data={sparkData.map((v) => ({ v }))}>
          <Line
            type="monotone"
            dataKey="v"
            stroke="hsl(var(--danger))"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      )}
    </div>
  )
}
